import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CircularProgress,
    Box,
    IconButton
} from '@mui/material';
import {
    Warning,
    Error,
    Info,
    CheckCircle,
    Close
} from '@mui/icons-material';
import { COLORS } from '../../settings/themeSettings';

/**
 * Composant Dialog de confirmation réutilisable
 * Remplace les window.confirm par une interface Material-UI moderne
 */
const ConfirmDialog = ({
    open = false,
    title = 'Confirmation',
    message = 'Êtes-vous sûr de vouloir continuer ?',
    confirmText = 'Confirmer',
    cancelText = 'Annuler',
    onConfirm,
    onCancel,
    severity = 'warning', // 'warning' | 'error' | 'info' | 'success'
    loading = false,
    maxWidth = 'sm'
}) => {
    const getIcon = () => {
        const iconProps = { sx: { fontSize: 48, mb: 2 } };

        switch (severity) {
            case 'error':
                return <Error {...iconProps} color="error" />;
            case 'success':
                return <CheckCircle {...iconProps} color="success" />;
            case 'info':
                return <Info {...iconProps} color="info" />;
            case 'warning':
            default:
                return <Warning {...iconProps} color="warning" />;
        }
    };

    const getConfirmColor = () => {
        switch (severity) {
            case 'error':
                return 'error';
            case 'success':
                return 'success';
            case 'info':
                return 'info';
            case 'warning':
            default:
                return 'warning';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onCancel}
            maxWidth={maxWidth}
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                }
            }}
        >
            <DialogTitle sx={{
                position: 'relative',
                pb: 1,
                fontWeight: 600
            }}>
                {title}
                {!loading && (
                    <IconButton
                        aria-label="close"
                        onClick={onCancel}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>
                )}
            </DialogTitle>

            <DialogContent>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    py: 2
                }}>
                    {getIcon()}
                    <DialogContentText sx={{ fontSize: '1rem', color: COLORS.text.primary }}>
                        {message}
                    </DialogContentText>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
                <Button
                    onClick={onCancel}
                    disabled={loading}
                    variant="outlined"
                    sx={{ minWidth: 100 }}
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color={getConfirmColor()}
                    disabled={loading}
                    sx={{ minWidth: 100 }}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {loading ? 'Traitement...' : confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDialog;
